<template>
  <map-container>
    <PacificGrid v-if="region === 'pacific'" />
    <regional-map-chart
      :data="chartData"
      :region="chartRegion"
      :stroke="stroke"
      :stroke-width="strokeWidth"
      :fill="fill"
      :regionClick="regionClick"
    />
  </map-container>
</template>

<script>
import { path, prop, filter, propEq, map } from 'ramda';
import { scaleLog } from 'd3-scale';
import { extent } from 'd3-array';
import { interpolateRgb } from 'd3-interpolate';
import { mapGetters, mapMutations } from 'vuex';

import { USA } from '@shared/constants';
import { MAP_COLORS } from '@shared/colors';
import { RegionalMapChart } from '@shared/components/charts';

import { PREFIX as REGIONS_STORE } from '../../../modules/regions';
import { TOGGLE_SELECTED_FILTER } from '@/modules/regions/regions.mutations';
import { GET_SELECTED_FILTERS, GET_SELECTED_SUBTOPIC, GET_YEAR_FILTER } from '@/modules/regions/regions.getters';
import { MAP_REGIONS, STROKE_WIDTH, SELECTED_STROKE_WIDTH } from './map.constants';
import { MapContainer } from './map.styles';
import { getRegionField, getSummedData, getValueField } from '../../utils/helpers';
import { YEAR_FIELD } from '../../constants';
import PacificGrid from '../pacificGrid/pacificGrid.component.vue';

export default {
  props: {
    region: { type: String, default: USA },
    theme: { type: Object, default: () => {} },
    data: { type: Array, default: () => [] },
  },
  components: { RegionalMapChart, MapContainer, PacificGrid },
  methods: {
    ...mapMutations(REGIONS_STORE, [TOGGLE_SELECTED_FILTER]),
    regionClick(data) {
      const regionField = getRegionField(this[GET_SELECTED_SUBTOPIC]);
      const region = data[regionField];
      if (region) {
        this[TOGGLE_SELECTED_FILTER](region);
      }
    },
    isSelected(data) {
      const id = path(['data', this.axis], data);

      if (!id) return false;
      return this.getSelectedFilters.indexOf(id) >= 0;
    },
  },
  computed: {
    ...mapGetters(REGIONS_STORE, [GET_SELECTED_FILTERS, GET_SELECTED_SUBTOPIC, GET_YEAR_FILTER]),
    chartData() {
      const valueField = getValueField(this[GET_SELECTED_SUBTOPIC]);
      const regionField = getRegionField(this[GET_SELECTED_SUBTOPIC]);
      const filteredData = filter(propEq(YEAR_FIELD, this[GET_YEAR_FILTER]), this.data);

      const summedData = getSummedData({ data: filteredData, groupByField: regionField, valueField });

      return {
        series: [valueField],
        axis: regionField,
        data: summedData,
      };
    },
    chartRegion() {
      return MAP_REGIONS[this.region] || MAP_REGIONS[USA];
    },
    axis() {
      return this.chartData.axis;
    },
    hasAnySelectedFilter() {
      return !!this.getSelectedFilters.length;
    },
    stroke() {
      return (data) =>
        !this.hasAnySelectedFilter || this.isSelected(data) ? MAP_COLORS.STROKE : MAP_COLORS.INACTIVE_STROKE;
    },
    strokeWidth() {
      return (data) => (this.hasAnySelectedFilter && this.isSelected(data) ? SELECTED_STROKE_WIDTH : STROKE_WIDTH);
    },
    colorScale() {
      const { data } = this.chartData;
      const valueField = getValueField(this[GET_SELECTED_SUBTOPIC]);
      const values = map(prop(valueField), data);
      const [minValue, maxValue] = extent(values);
      const colorScale = interpolateRgb(MAP_COLORS.LOW_VALUE, this.theme.mainColor);
      const offset = minValue ? 0 : 1;
      const scale = scaleLog()
        .domain([minValue + offset, maxValue + offset])
        .range([0, 1]);

      return (metric) => colorScale(scale(metric));
    },
    fill() {
      return (data) => {
        if (this.hasAnySelectedFilter && !this.isSelected(data)) return MAP_COLORS.INACTIVE_FILL;

        const valueField = getValueField(this[GET_SELECTED_SUBTOPIC]);
        const rank = path(['value', valueField], data);

        return rank ? this.colorScale(rank) : MAP_COLORS.NO_DATA;
      };
    },
  },
};
</script>
